<template>
    <RouterLink class="blocks__block" :style="{color: course.color}" :class="{unpaid: !course.paid}"
                :to="!course.has_access || !link ? '#' : link">
        <div class="block__content">
            <div class="content__header">{{ course.name }}</div>
            <div class="content__text">{{ course.ships.join(', ') }}</div>
            <template v-if="course.paid">
                <span v-if="course.has_access" class="button primary zoom documents-link">Лекции</span>
                <span v-else-if="course.prolongation_price > 0 && course.prolongation_months > 0"
                      @click="openProlongationModal()"
                      class="button primary zoom documents-link"
                >Пролонгация</span>
            </template>
            <RouterLink
                    v-if="nearLessonId"
                    class="button secondary zoom documents-link"
                    :to="{name: 'lesson', params: {id: course.id, lessonId: nearLessonId}}"
            >Zoom
            </RouterLink>
            <template v-if="buyAdditional && course.has_access">
                <br>
                <button @click.prevent="buyAdditionalModal = true" class="button primary zoom documents-link">Расширить курс</button>
            </template>
            <div v-if="!course.paid">
                <div class="need-pay">
                    <i class="far fa-clock"></i>
                    <span class="need-pay__text">Курс ожидает оплаты<template v-if="showCountToPay"> ({{ countToPay.minutes }}:{{ countToPay.seconds }})</template></span>
                </div>
                <button class="button primary sm wide">Оплатить</button>
            </div>
            <div v-if="!course.has_access">
                <div class="need-pay">
                    <i class="far fa-clock"></i>
                    <span class="need-pay__text">Время действия курса истекло</span>
                </div>
            </div>
        </div>
        <div class="block__progress" v-if="course.paid && !course.no_dates">
            <ProgressRound :progress="course.progress" :color="course.color"/>
        </div>
    </RouterLink>
    <BaseModal :show="prolongationModal"
               @cancel-modal="prolongationModal = false"
               title="Пролонгация">
        <template #description-slot>
            <p>
                Стоимость пролонгации <b>{{ numberFormat(course.prolongation_price) }} <CurrencyRuble/></b><br>
                После подтверждения вы будете перенаправлены на страницу оплаты
            </p>
            <br>
            <div class="form-group field" v-if="prolongationGroups.length">
                <label class="label">
                    Выберите группу для пролонгации:
                    <span class="input-wrapper">
                        <select class="input" v-model="prolongationGroup">
                            <option :value="group.id" v-for="group in prolongationGroups" :key="group.id">
                                <template v-if="group.date_of && group.date_to">
                                    с {{ group.date_of }} по {{ group.date_to }}
                                </template>
                                <template v-else>
                                    Группа без дат
                                </template>
                            </option>
                        </select>
                    </span>
                </label>
            </div>
            <div v-else>
                <div class="alert-transfer">Нет предстоящих групп в курсе</div>
            </div>
        </template>
        <a :href="prolongationLink" class="button primary" :class="{'disabled': !prolongationGroup}" :disabled="!prolongationGroup">Подтвердить</a>
        <button class="button secondary" @click="prolongationModal = false">Закрыть</button>
    </BaseModal>
    <BaseModal :show="buyAdditionalModal"
               @cancel-modal="buyAdditionalModal = false"
               title="Расширение курса">
        <template #description-slot>
            <p>Вы можете расширить свой курс, докупив позиции ниже.</p>
            <template v-for="custom in course.additional_fields" :key="custom.id">
                <template v-if="custom.fields.length">
                    <h3>{{ custom.title }}</h3>
                    <div v-for="field in custom.fields" :key="field.id">
                        <label>
                            <input type="checkbox" :checked="buyAdditionalArray.indexOf(field) !== -1" @change="buyAdditionalChange(field)"> {{ field.title }} (<b>{{ numberFormat(field.price ) }} <CurrencyRuble/></b>)
                        </label>
                    </div>
                </template>
            </template>
            <br>
            <p>Сумма к оплате: <b>{{ numberFormat(buyAdditionalPrice) }} <CurrencyRuble/></b></p>
            <p>После подтверждения вы будете перенаправлены на страницу оплаты.</p>
            <br>
        </template>
        <a :href="buyAdditionalLink" class="button primary" :class="{'disabled': buyAdditionalPrice === 0}" :disabled="buyAdditionalPrice === 0">Подтвердить</a>
        <button class="button secondary" @click="buyAdditionalModal = false;buyAdditionalArray.splice(0)">Закрыть</button>
    </BaseModal>
</template>

<script>
    import {apiHost} from "@/config";
    import numberFormat from "@/helpers/numberFormat";
    import ProgressRound from "@/components/common/ProgressRound";
    import { computed, ref, watch, onBeforeUnmount } from "vue";
    import {stringToTime} from "@/helpers/date";
    import BaseModal from "@/components/common/BaseModal";
    import CurrencyRuble from "@/components/common/CurrencyRuble";
    import {getCourses} from "@/data/requests";

    export default {
        props: ['course'],
        components: {ProgressRound, BaseModal, CurrencyRuble},
        setup(props) {
            let prolongationModal = ref(false);
            let prolongationGroup = ref(null);
            let prolongationGroups = ref([]);
            let buyAdditionalModal = ref(false);
            let buyAdditionalArray = ref([]);
          const countToPay = ref({ minutes: "00", seconds: "00" });
          const secondsLeft = ref(0);
          const timerId = ref(null);

          const showCountToPay = computed(() => {
            return !props.course?.paid && secondsLeft.value > 0;
          });

          const setDisplayTime = (total) => {
            const safe = Math.max(0, Math.floor(Number(total) || 0));
            const minutes = Math.floor(safe / 60);
            const seconds = safe % 60;

            countToPay.value.minutes = String(minutes).padStart(2, "0");
            countToPay.value.seconds = String(seconds).padStart(2, "0");
          };

          const stopTimer = () => {
            if (timerId.value) {
              clearInterval(timerId.value);
              timerId.value = null;
            }
          };

          const startTimer = () => {
            stopTimer();

            if (props.course?.paid) return;
            if (secondsLeft.value <= 0) return;

            timerId.value = setInterval(() => {
              if (secondsLeft.value > 0) {
                secondsLeft.value -= 1;
                setDisplayTime(secondsLeft.value);
              }

              if (secondsLeft.value <= 0) {
                stopTimer(); // просто останавливаем, без reload
              }
            }, 1000);
          };

          const initTimer = () => {
            const raw = Number(props.course?.count_seconds_to_pay ?? 0);
            secondsLeft.value = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
            setDisplayTime(secondsLeft.value);
            startTimer();
          };

          watch(
              () => props.course?.count_seconds_to_pay,
              () => initTimer(),
              { immediate: true }
          );

          watch(
              () => props.course?.paid,
              (paid) => {
                if (paid) stopTimer();
                else initTimer();
              }
          );

          onBeforeUnmount(() => stopTimer());

          const nearLessonId = computed(() => {
              const getCurrentOrNextLesson = (lessons, now = new Date()) => {
                const toDate = (dateStr, timeStr) => {
                  // date: DD.MM.YYYY, time: HH:MM
                  const [d, m, y] = dateStr.split('.').map(Number);
                  const [hh, mm] = timeStr.split(':').map(Number);
                  return new Date(y, m - 1, d, hh, mm, 0, 0); // локальное время
                };

                const items = lessons
                    .map((l, idx) => {
                      const start = toDate(l.date, l.time_of);
                      let end = toDate(l.date, l.time_to);
                      // На случай, если time_to "перешёл" за полночь (редко, но на всякий)
                      if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
                      return { ...l, _start: start, _end: end, _idx: idx };
                    })
                    // отсекаем битые даты
                    .filter(x => !Number.isNaN(x._start.getTime()) && !Number.isNaN(x._end.getTime()));

                // 1) Идёт сейчас
                const ongoing = items
                    .filter(x => x._start <= now && now < x._end)
                    .sort((a, b) => a._start - b._start)[0];
                if (ongoing) return ongoing; // вернёт объект занятия (можно удалить служебные поля, если мешают)

                // 2) Ближайшее будущее
                const upcoming = items
                    .filter(x => x._start > now)
                    .sort((a, b) => a._start - b._start)[0];

                return upcoming || null;
              }


              const lessons = props.course?.lessons.filter(l => l.zoom_link && l.date && l.time_of && l.time_to)

              return getCurrentOrNextLesson(lessons)?.id
            })

            const token = ref(localStorage.getItem("token"));

            const link = computed(() => {
                const course = props.course;
                if(!course.lessons.length) return false;

                if (!course.paid) return {name: 'payment', params: {id: course.group_id}};
                const lessons = course.lessons.filter(lesson => stringToTime(lesson.date, lesson.time_of) > Date.now());
                const lessonId = lessons.length ? lessons[0].id : course.lessons[0].id;
                return {name: 'lesson', params: {id: course.id, lessonId}};
            });

            const prolongationLink = computed(() => {
                return `${apiHost}courses/${props.course.group_id}/prolongation/${prolongationGroup.value}?api_token=${token.value}`;
            });

            const buyAdditionalPrice = computed(() => {
                return buyAdditionalArray.value.reduce((accumulator, field) => {
                    return accumulator + field.price;
                }, 0)
            });

            const buyAdditionalLink = computed(() => {
                let link = `${apiHost}courses/${props.course.group_id}/additional?api_token=${token.value}`;

                buyAdditionalArray.value.forEach(field => {
                    link += `&custom[${field.custom_id}][]=${field.id}`;
                });

                return link;
            });

            const buyAdditional = ref(props.course.additional_fields.length && props.course.additional_fields.find(field => field.fields.length));

            return {countToPay, showCountToPay, link, prolongationModal, prolongationGroup, numberFormat, prolongationGroups, prolongationLink, buyAdditional, buyAdditionalModal, buyAdditionalArray, buyAdditionalPrice, buyAdditionalLink, nearLessonId};
        },
        methods: {
            async openProlongationModal() {
                const courses = await getCourses(this.token);
                const groups = courses.find(course => this.course.id === course.id)?.groups ?? [];
                this.prolongationGroups = groups.filter(group => ((group.no_dates && group.id == this.course.group_id) || (!group.no_dates && group.id != this.course.group_id)));
                this.prolongationModal = true;
            },
            buyAdditionalChange(field) {
                const fieldIndex = this.buyAdditionalArray.indexOf(field);
                if(fieldIndex !== -1) {
                    this.buyAdditionalArray.splice(fieldIndex, 1);
                } else {
                    this.buyAdditionalArray.push(field);
                }
            },
        },
    }
</script>

<style scoped lang="scss">
    .unpaid {
        &::before {
            opacity: .3;
        }
    }

    .need-pay {
        margin-top: 18px;
        margin-bottom: 18px;
        display: flex;
        align-items: center;

        .fa-clock {
            color: #C7CDD1;
            font-size: 24px;
            margin-right: 7px;
        }

        &__text {
            font-weight: 500;
            font-size: 14px;
            line-height: 24px;
            color: #282C2E;
        }
    }

    .documents-link {
        font-size: 10px;
        padding: 5px 15px 5px 15px;
        margin-top: 10px;
        margin-left: 5px;
    }
    .alert-transfer {
        background: #ffdbdb;
        padding: 10px;
        border-radius: 4px;
        margin-top: 20px;
    }
</style>
